import React, { useState, useEffect, useRef } from 'react';
import { styled } from '@mui/material/styles';
import { Box, Drawer as MuiDrawer, AppBar as MuiAppBar, Toolbar, List, CssBaseline, Typography, IconButton, ListItem, ListItemButton, ListItemIcon, ListItemText, InputBase, Collapse, Avatar, Menu, MenuItem, Modal, Divider, TextField, Button, CircularProgress } from '@mui/material';
import { Search as SearchIcon, ExpandLess, ExpandMore, ImageOutlined } from '@mui/icons-material';
import { CiCircleInfo } from 'react-icons/ci';
import { useNavigate } from 'react-router-dom';
import { NCRSideBarListItemText } from '../Utilities/Text';
import { useDispatch, useSelector } from 'react-redux';
import { fetchUserData, logout } from '../../Helpers/userSlice';
import { fetchTargets, saveTargets, updateTargets, clearSuccess, clearError, clearAgingTargets } from '../../Helpers/targetSlice';
import { setNCProMenuOpen, setReportsMenuOpen } from '../../Helpers/menuSlice';

import useCheckUserToken from "../../Helpers/Common";
import axios from 'axios';
import Loader from './Loader';
import toast from 'react-hot-toast';
import { ModalClose, Sheet } from '@mui/joy';
import { useParams } from "react-router-dom";
import { getHeaders } from '../../Helpers/NCRHelpers';
import { fetchDashboardData,resetDashboardData } from '../../Helpers/dashboardSlice';
import { processFileForUpload } from '../../utils/imageCompressor';

const drawerWidth = 240;

const AddUserLabel = ({ sx, children }) => {
  return (
    <Typography
      className='add-user-label'
      variant='h2'
      sx={{
        fontFamily: 'Lato, sans-serif',
        fontSize: '14px',
        fontWeight: '500',
        lineHeight: '16.8px',
        color: '#000000',
        mb: 1,
        ...sx,
        '& span': {
          fontSize: '12px',
          color: 'gray'
        },
      }}
    >
      {children}
    </Typography>
  );
};

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: 'hidden',
});

const closedMixin = (theme) => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(10)} + 1px)`,
  },
});

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-end',
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

const AppBar = styled(MuiAppBar, {
  shouldForwardProp: (prop) => prop !== 'open',
})(({ theme }) => ({
  zIndex: theme.zIndex.drawer + 1,
  transition: theme.transitions.create(['width', 'margin'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  variants: [
    {
      props: ({ open }) => open,
      style: {
        marginLeft: drawerWidth,
        width: `calc(100% - ${drawerWidth}px)`,
        transition: theme.transitions.create(['width', 'margin'], {
          easing: theme.transitions.easing.sharp,
          duration: theme.transitions.duration.enteringScreen,
        }),
      },
    },
  ],
}));

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme }) => ({
    width: drawerWidth,
    flexShrink: 0,
    whiteSpace: 'nowrap',
    boxSizing: 'border-box',
    variants: [
      {
        props: ({ open }) => open,
        style: {
          ...openedMixin(theme),
          '& .MuiDrawer-paper': openedMixin(theme),
        },
      },
      {
        props: ({ open }) => !open,
        style: {
          ...closedMixin(theme),
          '& .MuiDrawer-paper': closedMixin(theme),
        },
      },
    ],
  }),
);

export default function DashboardLayout({ content }) {
  const [open, setOpen] = React.useState(true);
  const [isProfileOpen, setIsProfileOpen] = React.useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const isNCProMenuOpen = useSelector((state) => state.menu.isNCProMenuOpen);
  const isReportsMenuOpen = useSelector((state) => state.menu.isReportsMenuOpen);
  const isContentSelected = (label) => ["View", "Initiate", "Edit", "Action", "Review", "Closure"].some((keyword) => label.includes(keyword));
  const { id } = useParams();

  const { userData, isAuthenticated, isLoading } = useSelector((state) => state.user);
  const [searchTerm, setSearchTerm] = useState("");
  const [matchCount, setMatchCount] = useState(0);

  const [anchorEl, setAnchorEl] = useState(null);
  const dropdownOpen = Boolean(anchorEl);

  const accessToken = localStorage.getItem('NCProAccessToken');
  const refreshToken = localStorage.getItem('NCProRefreshToken');
  const { isExpired } = useCheckUserToken(accessToken);

  const [target, setTarget] = useState({
    aging_target_review: 0,
    aging_target_action: 0
  });
  const [errors, setErrors] = useState({ actionTarget: false, reviewTarget: false });
  const [isProfileIconHovered, setIsProfileIconHovered] = useState(false);
  const isEditRef = useRef(false);

  const { targetData, successMessage, error } = useSelector((state) => state.target);
  const selectedFinancialYear = useSelector(state => state.dashboard.selectedFinancialYear);
  const [profilePic, setProfilePic] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  const [originalPicUrl, setOriginalPicUrl] = useState('');
  const [headerAvatar, setHeaderAvatar] = useState('');
  const [isImageRemoved, setIsImageRemoved] = useState(false);
  const [isCompressingProfile, setIsCompressingProfile] = useState(false);
  const fileInputRef = useRef(null);

  const fetchProfilePicture = async () => {
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/settings/profile-picture`,
        getHeaders()
      );

      const imageUrl = res?.data?.data?.url || '';
      setHeaderAvatar(imageUrl);
      setPreviewUrl(imageUrl);
      setOriginalPicUrl(imageUrl);
    } catch {
      setHeaderAvatar('');
      setPreviewUrl('');
      setOriginalPicUrl('');
    }
  };


  useEffect(() => {
    if (accessToken) {
      fetchProfilePicture();
    }
  }, [accessToken]);

  useEffect(() => {
    if (isProfileOpen) {
      fetchProfilePicture();
    }
  }, [isProfileOpen]);


  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const allowedTypes = ["image/jpeg", "image/png"];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Only JPG, JPEG, and PNG files are allowed");
      e.target.value = "";
      return;
    }

    const fileSizeMB = file.size / (1024 * 1024);

    // If file is over 10MB, reject completely
    if (fileSizeMB > 10) {
      toast.error("Image size should not exceed 10MB!");
      e.target.value = "";
      return;
    }

    // If file is between 2MB and 10MB, compress it
    if (fileSizeMB > 2) {
      setIsCompressingProfile(true);
      try {
        const { file: processedFile, wasCompressed } = await processFileForUpload(file, 2, 10);
        if (wasCompressed) {
          toast.success(`Image compressed from ${fileSizeMB.toFixed(1)}MB to ${(processedFile.size / (1024 * 1024)).toFixed(1)}MB`);
        }
        setProfilePic(processedFile);
        setPreviewUrl(URL.createObjectURL(processedFile));
        setIsImageRemoved(false);
      } catch (error) {
        toast.error(error.message || "Failed to compress image");
      } finally {
        setIsCompressingProfile(false);
      }
      e.target.value = "";
      return;
    }

    // File is under 2MB, use as is
    setProfilePic(file);
    setPreviewUrl(URL.createObjectURL(file));
    setIsImageRemoved(false);
    e.target.value = "";
  };

  const handleRemove = () => {
    setPreviewUrl("");
    setProfilePic(null);
    setIsImageRemoved(true);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };


  const handleSaveProfile = async () => {
    try {
      if (profilePic) {
        const formData = new FormData();
        formData.append("profile_picture", profilePic);

        const res = await axios.post(
          `${process.env.REACT_APP_BACKEND_BASE_URL}/settings/profile-picture`,
          formData,
          { headers: { Authorization: `Bearer ${accessToken}` } }
        );

        const newUrl = res?.data?.data?.url;
        setHeaderAvatar(newUrl);
        setPreviewUrl(newUrl);
        setOriginalPicUrl(newUrl);
        setProfilePic(null);
        setIsImageRemoved(false);

        toast.success("Profile picture updated");
        return;
      }

      if (isImageRemoved && originalPicUrl) {
        await axios.delete(
          `${process.env.REACT_APP_BACKEND_BASE_URL}/settings/profile-picture`,
          getHeaders()
        );

        setHeaderAvatar("");
        setPreviewUrl("");
        setOriginalPicUrl("");
        setIsImageRemoved(false);

        toast.success("Profile picture removed");
      }
    } catch (err) {
      const msg =
        err?.response?.data?.errorDetails?.message ||
        err?.response?.data?.message ||
        err?.response?.data?.error ||
        "Profile update failed";

      toast.error(msg);
    }
  };

  const fetchSettings = async (clientId = '') => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_BACKEND_BASE_URL}/email/${clientId}`, getHeaders());
      if (response?.data?.data) {
        isEditRef.current = true;
      }
    } catch (error) {
      return [];
    }
  };

  useEffect(() => {
    const resetHighlighting = () => {
      document.querySelectorAll(".highlight").forEach(el => {
        el.outerHTML = el.innerText;
      });
    };

    const escapeRegExp = (string) => {
      return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    };

    const highlightMatches = () => {
      if (!searchTerm.trim()) {
        resetHighlighting();
        setMatchCount(0);
        return;
      }

      const safeSearchTerm = escapeRegExp(searchTerm);
      const regex = new RegExp(safeSearchTerm, "gi");
      let count = 0;

      const walkTree = (node) => {
        if (node.nodeType === 3) {
          const matches = node.nodeValue.match(regex);
          if (matches) {
            count += matches.length;
            const span = document.createElement("span");
            span.className = "highlight";
            span.style.backgroundColor = "yellow";
            span.style.fontWeight = "bold";
            span.textContent = node.nodeValue;
            node.replaceWith(span);
          }
        } else {
          node.childNodes.forEach(walkTree);
        }
      };

      resetHighlighting();
      document.body.childNodes.forEach(walkTree);
      setMatchCount(count);
    };

    highlightMatches();
  }, [searchTerm]);

  useEffect(() => {
    const refreshTokenAndFetchUserData = async () => {
      if (isExpired) {
        if (refreshToken) {
          try {
            const response = await axios.get(
              `${process.env.REACT_APP_BACKEND_BASE_URL}/user/refresh-token`,
              {
                headers: {
                  Authorization: `Bearer ${refreshToken}`,
                },
              }
            );
            const newAccessToken = response.data.accessToken;
            localStorage.setItem('NCProAccessToken', newAccessToken);
            dispatch(fetchUserData(newAccessToken));
          } catch (error) {
            if (error.response.data.errorDetails.message === 'jwt expired') {
              localStorage.removeItem('NCProAccessToken');
              localStorage.removeItem('NCProRefreshToken');
            }
            dispatch(logout());
            navigate('/login');
          }
        }
      } else {
        if (accessToken) {
          dispatch(fetchUserData(accessToken));
        }
      }
    };
    if (!isAuthenticated) {
      refreshTokenAndFetchUserData();
    }
  }, [isAuthenticated, accessToken, refreshToken, isExpired, dispatch, navigate]);

  useEffect(() => {
    if (!accessToken || !refreshToken) {
      toast.error("Please login");
      navigate('/login');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  useEffect(() => {
    if (isContentSelected(content.label)) {
      dispatch(setNCProMenuOpen(true));
    }

    if (content.label.includes("8D report") || content.label.includes("Data table")) {
      dispatch(setReportsMenuOpen(true));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (accessToken && userData?.clientId) {
      dispatch(fetchTargets(userData?.clientId, accessToken));
    }
  }, [accessToken, userData?.clientId, dispatch]);

  useEffect(() => {
    setTarget({
      aging_target_action: targetData.aging_target_action,
      aging_target_review: targetData.aging_target_review,
    });
  }, [targetData]);

  useEffect(() => {
    if (successMessage) {
      toast.success(successMessage);
      dispatch(clearSuccess());
      setIsProfileOpen(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [successMessage]);

  useEffect(() => {
    if (error) {
      toast.error(error);
      dispatch(clearError());
      setIsProfileOpen(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  useEffect(() => {
    const setTargets = async () => {
      if (isProfileOpen) {
        setTarget({
          aging_target_action: targetData.aging_target_action ?? 0,
          aging_target_review: targetData.aging_target_review ?? 0,
        });
        await fetchSettings(userData.clientId);
      }
    }
    setTargets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProfileOpen]);

  const handleTargetChange = (field) => (e) => {
    const value = e.target.value;
    const newVal = value === "" ? null : Number(value);

    setTarget((prev) => ({
      ...prev,
      [field]: newVal,
    }));

    if (newVal !== null) {
      setErrors((prev) => ({
        ...prev,
        [field === "aging_target_action" ? "actionTarget" : "reviewTarget"]: false,
      }));
    }
  };

  const handleCollapseClick = () => {
    setOpen((prev) => !prev);
    dispatch(setNCProMenuOpen(false));
    dispatch(setReportsMenuOpen(false));
  };

  const handleIsNCProMenuOpen = () => {
    dispatch(setNCProMenuOpen(!isNCProMenuOpen));
    setOpen(true);
  };

  const handleIsReportsMenuOpen = () => {
    dispatch(setReportsMenuOpen(!isReportsMenuOpen));
    setOpen(true);
  };

  if (isLoading) {
    return (
      <Loader /> // Later, will handle this as a common component in Redux
    );
  }

  const handleLogout = async () => {
    localStorage.removeItem('NCProAccessToken');
    localStorage.removeItem('NCProRefreshToken');
    dispatch(setNCProMenuOpen(false));
    dispatch(setReportsMenuOpen(false));
    dispatch(clearAgingTargets());
    dispatch(logout());
    dispatch(resetDashboardData());
    navigate('/login');
  };

  const handleProfileClick = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMyProfileClick = async () => {
    setAnchorEl(null);
    setIsProfileOpen(true);
    setErrors({ actionTarget: false, reviewTarget: false });
    await dispatch(fetchTargets(userData?.clientId, accessToken));
  };

  const handleTarget = async () => {
    let newErrors = {};

    if (target.aging_target_action === null)
      newErrors.actionTarget = true;
    if (target.aging_target_review === null)
      newErrors.reviewTarget = true;

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error("Please fill in all required fields.");
      return;
    }
    const data = {
      client_id: userData?.clientId,
      ...Object.fromEntries(
        Object.entries(target).filter(([_, value]) => value !== "" && value != null)
      )
    };

    if (!isEditRef.current) {
      await dispatch(saveTargets(data, accessToken));
    }
    else {
      await dispatch(updateTargets(data, accessToken));
    }
    dispatch(fetchDashboardData({
      startDate: selectedFinancialYear.startDate,
      endDate: selectedFinancialYear.endDate
    }));
    handleSaveProfile()
  }

  return (
    <Box>
      {userData && (
        <Box sx={{ display: 'flex' }}>
          <CssBaseline />
          <AppBar
            position="fixed"
            open={open}
            sx={{
              background: 'rgba(255, 255, 255, 1)',
              boxShadow: 'none',
              borderBottom: '1px solid rgba(224, 224, 224, 1)',
              minHeight: '60px',
              py: 0.8
            }}
          >
            <Toolbar sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              {!open && (
                <Box
                  onClick={() => navigate('/ncpro/welcome')}
                  sx={{ cursor: 'pointer' }}
                >
                  <img
                    src="/images/dashboardlogo.png"
                    alt="Banner"
                    style={{ maxHeight: 100 }}
                  />
                </Box>
              )}
              <Box
                sx={{
                  flex: 1,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  opacity: id ? 0 : 1,
                  pointerEvents: id ? 'none' : 'auto',
                  transition: 'opacity 0.3s ease',
                }}
              >
                <InputBase
                  sx={{
                    fontFamily: 'Lato, sans-serif',
                    width: '40%',
                    border: '1px solid rgba(211, 211, 211, 1)',
                    background: 'rgba(240, 240, 240, 1)',
                    padding: '0 10px',
                    display: 'flex',
                    alignItems: 'center',
                    borderRadius: '200px',
                    py: 0.5,
                    px: 2,
                    // borderRight: '200px'
                  }}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search any keyword"
                  inputProps={{ 'aria-label': 'search google maps' }}
                  startAdornment={
                    <IconButton sx={{ padding: 0 }} aria-label="search">
                      <SearchIcon sx={{ color: 'rgba(137, 137, 137, 1)', m: 1 }} />
                    </IconButton>
                  }
                  endAdornment={
                    searchTerm && (
                      <Box sx={{ display: 'flex', alignItems: 'center', mr: 1 }}>
                        <Typography sx={{ fontSize: '12px', color: '#777' }}>
                          ({matchCount})
                        </Typography>
                      </Box>
                    )
                  }
                />
              </Box>
              <Box>
                <Box sx={{ display: "flex", alignItems: "center", cursor: "pointer" }} onClick={handleProfileClick}>
                  <Avatar
                    sx={{ marginRight: 1 }}
                    src={headerAvatar || undefined}
                  />
                  <Box >
                    <Typography
                      variant="h6"
                      sx={{ fontFamily: "Lato, sans-serif", fontSize: "14px", fontWeight: "400", color: "rgba(15, 15, 15, 1)" }}
                    >
                      {userData.fullName || "Unknown User"}
                    </Typography>
                    <Typography
                      variant="h6"
                      sx={{ fontFamily: "Lato, sans-serif", fontSize: "14px", fontWeight: "700", color: "rgba(84, 84, 84, 1)" }}
                    >
                      {userData.roleCode === "SUPADM"
                        ? "Super Admin"
                        : userData.roleCode === "CLTADM"
                          ? "Admin"
                          : userData.roleCode === "CLTUSR"
                            ? "User"
                            : "Unknown Role"}
                    </Typography>
                  </Box>
                </Box>
                <Menu
                  anchorEl={anchorEl}
                  open={dropdownOpen}
                  onClose={() => setAnchorEl(null)}
                  anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                  transformOrigin={{ vertical: "top", horizontal: "right" }}
                  disableAutoFocusItem
                  container={document.body}
                  disableScrollLock
                >
                  <MenuItem className='default-text' onClick={handleMyProfileClick}>My Profile</MenuItem>
                  <MenuItem className='default-text' onClick={() => navigate('/change-password')}>Change Password</MenuItem>
                  <MenuItem className='default-text' onClick={() => handleLogout()}>Log out</MenuItem>
                </Menu>
              </Box>
            </Toolbar>
          </AppBar>
          <Drawer variant="permanent" open={open}
            PaperProps={{
              sx: {
                backgroundColor: "rgba(248, 248, 248, 1)",
                color: "rgba(84, 84, 84, 1)"
              }
            }}
          >
            <DrawerHeader sx={{ display: 'flex', justifyContent: 'flex-start' }}>
              <Box
                onClick={() => navigate('/ncpro/welcome')}
                sx={{ cursor: 'pointer' }}
              >
                <img
                  src="/images/dashboardlogo.png"
                  alt="Banner"
                  style={{ maxHeight: 100, paddingLeft: '10px' }}
                />
              </Box>
            </DrawerHeader>
            <Box sx={{ flexGrow: 1, }}>
              {(userData.roleCode.includes('CLTADM') || userData.roleCode.includes('CLTUSR')) && (
                <List
                  sx={{ width: '100%', maxWidth: 360, pt: 2, background: 'transparent' }}
                  component="nav"
                  aria-labelledby="nested-list-subheader"
                >
                  <ListItemButton
                    onClick={() => handleIsNCProMenuOpen()}
                    selected={isContentSelected(content.label)}
                    sx={{
                      mb: 1.5,
                      position: 'relative',
                      backgroundColor: isNCProMenuOpen ? 'rgba(212, 218, 255, 1) !important' : '',
                    }}
                  >
                    {isNCProMenuOpen && <span style={{ position: 'absolute', width: 10, height: '86%', backgroundColor: '#2A3DBD', left: -4, borderTopRightRadius: '10px', borderBottomRightRadius: '10px' }} />}
                    <ListItemIcon sx={{ pl: 1 }}>
                      <div
                        style={{
                          width: 24,
                          height: 24,
                          backgroundColor: isNCProMenuOpen ? "rgba(42, 61, 189, 1)" : "#545454",
                          WebkitMaskImage: "url('/images/Sidebar/ncpro.svg')",
                          maskImage: "url('/images/Sidebar/ncpro.svg')",
                          WebkitMaskRepeat: "no-repeat",
                          maskRepeat: "no-repeat",
                          WebkitMaskSize: "contain",
                          maskSize: "contain",
                          WebkitMaskPosition: "center",
                          maskPosition: "center",
                        }}
                      />
                    </ListItemIcon>
                    <NCRSideBarListItemText
                      primary="NC Pro"
                      props={{
                        sx: isNCProMenuOpen ? { fontWeight: 700, color: 'rgba(42, 61, 189, 1)' } : {},
                      }}
                      open={open}
                    />
                    {open ? isNCProMenuOpen ? <ExpandLess sx={{ color: '#2A3DBD', fontSize: '28px', fontWeight: 100 }} /> : <ExpandMore sx={{ fontSize: '28px', fontWeight: 100 }} /> : ''}
                  </ListItemButton>
                  <Collapse in={isNCProMenuOpen} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      <ListItemButton
                        onClick={() => navigate('/ncpro/view')}
                        selected={content.label.includes("View")}
                        sx={{
                          backgroundColor: content.label.includes("View") ? 'rgba(212, 218, 255, 1) !important' : '',
                        }}
                      >
                        <ListItemIcon>
                        </ListItemIcon>
                        <NCRSideBarListItemText
                          primary="View"
                          open={open}
                          props={{
                            sx: content.label.includes("View") ? { fontWeight: 700, color: 'rgba(42, 61, 189, 1)' } : {},
                          }}
                        />
                      </ListItemButton>
                      <ListItemButton
                        onClick={() => navigate('/ncpro/initiate')}
                        selected={content.label.includes("Initiate")}
                        sx={{
                          backgroundColor: content.label.includes("Initiate") ? 'rgba(212, 218, 255, 1) !important' : '',
                        }}
                      >
                        <ListItemIcon>
                        </ListItemIcon>
                        <NCRSideBarListItemText
                          primary="Initiate"
                          open={open}
                          props={{
                            sx: content.label.includes("Initiate") ? { fontWeight: 700, color: 'rgba(42, 61, 189, 1)' } : {},
                          }}
                        />
                      </ListItemButton>
                      <ListItemButton
                        onClick={() => navigate('/ncpro/edit')}
                        selected={content.label.includes("Edit")}
                        sx={{
                          backgroundColor: content.label.includes("Edit") ? 'rgba(212, 218, 255, 1) !important' : '',
                        }}
                      >
                        <ListItemIcon>
                        </ListItemIcon>
                        <NCRSideBarListItemText
                          primary="Edit"
                          open={open}
                          props={{
                            sx: content.label.includes("Edit") ? { fontWeight: 700, color: 'rgba(42, 61, 189, 1)' } : {},
                          }}
                        />
                      </ListItemButton>
                      <>
                        <ListItemButton
                          onClick={() => navigate('/ncpro/action')}
                          selected={content.label.includes("Action")}
                          sx={{
                            backgroundColor: content.label.includes("Action") ? 'rgba(212, 218, 255, 1) !important' : '',
                          }}
                        >
                          <ListItemIcon>
                          </ListItemIcon>
                          <NCRSideBarListItemText
                            primary="Action"
                            open={open}
                            props={{
                              sx: content.label.includes("Action") ? { fontWeight: 700, color: 'rgba(42, 61, 189, 1)' } : {},
                            }}
                          />
                        </ListItemButton>
                        <ListItemButton
                          onClick={() => navigate('/ncpro/review')}
                          selected={content.label.includes("Review")}
                          sx={{
                            backgroundColor: content.label.includes("Review") ? 'rgba(212, 218, 255, 1) !important' : '',
                          }}
                        >
                          <ListItemIcon>
                          </ListItemIcon>
                          <NCRSideBarListItemText
                            primary="Review"
                            open={open}
                            props={{
                              sx: content.label.includes("Review") ? { fontWeight: 700, color: 'rgba(42, 61, 189, 1)' } : {},
                            }}
                          />
                        </ListItemButton>
                        <ListItemButton
                          onClick={() => navigate('/ncpro/closure')}
                          selected={content.label.includes("Closure")}
                          sx={{
                            backgroundColor: content.label.includes("Closure") ? 'rgba(212, 218, 255, 1) !important' : '',
                          }}
                        >
                          <ListItemIcon>
                          </ListItemIcon>
                          <NCRSideBarListItemText
                            primary="Closure"
                            open={open}
                            props={{
                              sx: content.label.includes("Closure") ? { fontWeight: 700, color: 'rgba(42, 61, 189, 1)' } : {},
                            }}
                          />
                        </ListItemButton>
                      </>
                    </List>
                  </Collapse>
                </List>
              )}

              {(userData.roleCode.includes('CLTADM') || userData.roleCode.includes('CLTUSR')) && (<List sx={{ pb: 2, mt: -1 }}>
                <ListItem disablePadding sx={{ display: 'block' }}>
                  <ListItemButton
                    onClick={() => navigate('/ncpro/analysis')}
                    selected={content.label.includes("Analysis")}
                    sx={[
                      {
                        minHeight: 48,
                        position: 'relative',
                        backgroundColor: content.label.includes("Analysis") ? 'rgba(212, 218, 255, 1) !important' : '',
                      },
                      open
                        ? {
                          justifyContent: 'initial',
                        }
                        : {
                          justifyContent: 'center',
                        },
                    ]}
                  >
                    {(content.label.includes("Analysis")) && <span style={{ position: 'absolute', width: 10, height: '86%', backgroundColor: '#2A3DBD', left: -4, borderTopRightRadius: '10px', borderBottomRightRadius: '10px' }} />}
                    <ListItemIcon
                      sx={[
                        {
                          minWidth: 0,
                          justifyContent: 'center',
                          pl: 1,
                        },
                        open
                          ? {
                            mr: 3,
                          }
                          : {
                            mr: 'auto',
                          },
                      ]}
                    >
                      <div
                        style={{
                          width: 24,
                          height: 24,
                          backgroundColor: content.label.includes("Analysis") ? "rgba(42, 61, 189, 1)" : "#545454",
                          WebkitMaskImage: "url('/images/Sidebar/users.svg')",
                          maskImage: "url('/images/Sidebar/monitor.svg')",
                          WebkitMaskRepeat: "no-repeat",
                          maskRepeat: "no-repeat",
                          WebkitMaskSize: "contain",
                          maskSize: "contain",
                          WebkitMaskPosition: "center",
                          maskPosition: "center",
                        }}
                      />
                    </ListItemIcon>
                    <ListItemText
                      primary={"RC Analysis"}
                      sx={{
                        opacity: open ? 1 : 0,
                        '& .MuiTypography-root': {
                          fontFamily: 'Lato, sans-serif',
                          fontWeight: content.label.includes("Analysis") ? 700 : 'normal',
                          color: content.label.includes("Analysis") ? 'rgba(42, 61, 189, 1)' : 'inherit',
                        },
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              </List>
              )}

              {(userData.roleCode.includes('CLTADM') || userData.roleCode.includes('CLTUSR')) && (
                <List
                  sx={{ width: '100%', maxWidth: 360, py: 0, background: 'transparent' }}
                  component="nav"
                  aria-labelledby="nested-list-subheader"
                >
                  <ListItemButton onClick={() => handleIsReportsMenuOpen()}
                    selected={content.label.includes("8D report") || content.label.includes("Data table")}
                    sx={{
                      mb: 1.5,
                      position: 'relative',
                      backgroundColor: isReportsMenuOpen ? 'rgba(212, 218, 255, 1) !important' : '',
                    }}>
                    {isReportsMenuOpen && <span style={{ position: 'absolute', width: 10, height: '86%', backgroundColor: '#2A3DBD', left: -4, borderTopRightRadius: '10px', borderBottomRightRadius: '10px' }} />}
                    <ListItemIcon sx={{ pl: 1 }}>
                      <div
                        style={{
                          width: 24,
                          height: 24,
                          backgroundColor: isReportsMenuOpen ? "rgba(42, 61, 189, 1)" : "#545454",
                          WebkitMaskImage: "url('/images/Sidebar/reports.png')",
                          maskImage: "url('/images/Sidebar/reports.png')",
                          WebkitMaskRepeat: "no-repeat",
                          maskRepeat: "no-repeat",
                          WebkitMaskSize: "contain",
                          maskSize: "contain",
                          WebkitMaskPosition: "center",
                          maskPosition: "center",
                        }}
                      />
                    </ListItemIcon>
                    <NCRSideBarListItemText
                      primary="Reports"
                      props={{
                        sx: isReportsMenuOpen ? { fontWeight: 700, color: 'rgba(42, 61, 189, 1)' } : {},
                      }}
                      open={open}
                    />
                    {isReportsMenuOpen ? <ExpandLess sx={{ color: '#2A3DBD', fontSize: '28px', fontWeight: 100 }} /> : <ExpandMore sx={{ fontSize: '28px', fontWeight: 100 }} />}
                  </ListItemButton>
                  <Collapse in={isReportsMenuOpen} timeout="auto" unmountOnExit>
                    <List component="div" disablePadding>
                      <ListItemButton
                        onClick={() => navigate('/report/8d')}
                        selected={content.label.includes("8D report")}
                        sx={{
                          backgroundColor: content.label.includes("8D report") ? 'rgba(212, 218, 255, 1) !important' : '',
                        }}>
                        <ListItemIcon>
                        </ListItemIcon>
                        <NCRSideBarListItemText
                          primary="8D report"
                          open={open}
                          props={{
                            sx: content.label.includes("8D report") ? { fontWeight: 700, color: 'rgba(42, 61, 189, 1)' } : {},
                          }}
                        />
                      </ListItemButton>
                      <ListItemButton
                        onClick={() => navigate('/report/datatable')}
                        selected={content.label.includes("Data table")}
                        sx={{
                          backgroundColor: content.label.includes("Data table") ? 'rgba(212, 218, 255, 1) !important' : '',
                        }}>
                        <ListItemIcon>
                        </ListItemIcon>
                        <NCRSideBarListItemText
                          primary="Data table"
                          open={open}
                          props={{
                            sx: content.label.includes("Data table") ? { fontWeight: 700, color: 'rgba(42, 61, 189, 1)' } : {},
                          }}
                        />
                      </ListItemButton>
                    </List>
                  </Collapse>
                </List>
              )}
              {(!userData.roleCode.includes('CLTUSR')) && (
                <List sx={{
                  pb: 1,
                  pt: userData.roleCode.includes('SUPADM') ? 5 : 0
                }}>
                  <ListItem disablePadding sx={{ display: 'block' }}>
                    <ListItemButton
                      onClick={() => navigate('/users')}
                      selected={content.label.includes("User Management")}
                      sx={[
                        {
                          minHeight: 48,
                          position: 'relative',
                          backgroundColor: content.label.includes("User Management") ? 'rgba(212, 218, 255, 1) !important' : '',
                        },
                        open
                          ? {
                            justifyContent: 'initial',
                          }
                          : {
                            justifyContent: 'center',
                          },
                      ]}
                    >
                      {(content.label.includes("User Management")) && <span style={{ position: 'absolute', width: 10, height: '86%', backgroundColor: '#2A3DBD', left: -4, borderTopRightRadius: '10px', borderBottomRightRadius: '10px' }} />}
                      <ListItemIcon
                        sx={[
                          {
                            minWidth: 0,
                            justifyContent: 'center',
                            pl: 1,
                          },
                          open
                            ? {
                              mr: 3,
                            }
                            : {
                              mr: 'auto',
                            },
                        ]}
                      >
                        <div
                          style={{
                            width: 24,
                            height: 24,
                            backgroundColor: content.label.includes("User Management") ? "rgba(42, 61, 189, 1)" : "#545454",
                            WebkitMaskImage: "url('/images/Sidebar/users.svg')",
                            maskImage: "url('/images/Sidebar/users.svg')",
                            WebkitMaskRepeat: "no-repeat",
                            maskRepeat: "no-repeat",
                            WebkitMaskSize: "contain",
                            maskSize: "contain",
                            WebkitMaskPosition: "center",
                            maskPosition: "center",
                          }}
                        />
                      </ListItemIcon>
                      <ListItemText
                        primary={"User management"}
                        sx={{
                          opacity: open ? 1 : 0,
                          '& .MuiTypography-root': {
                            fontFamily: 'Lato, sans-serif',
                            fontWeight: content.label.includes("User Management") ? 700 : 'normal',
                            color: content.label.includes("User Management") ? 'rgba(42, 61, 189, 1)' : 'inherit',
                          },
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                </List>
              )}
              {(userData.roleCode === 'SUPADM') && (
                <List sx={{ pb: 1, pt: 0 }}>
                  <ListItem disablePadding sx={{ display: 'block' }}>
                    <ListItemButton
                      onClick={() => navigate('/ncpro/settings')}
                      selected={content.label.includes("Settings")}
                      sx={[
                        {
                          minHeight: 48,
                          position: 'relative',
                          backgroundColor: content.label.includes("Settings") ? 'rgba(212, 218, 255, 1) !important' : '',
                        },
                        open
                          ? {
                            justifyContent: 'initial',
                          }
                          : {
                            justifyContent: 'center',
                          },
                      ]}
                    >
                      {(content.label.includes("Settings")) && <span style={{ position: 'absolute', width: 10, height: '86%', backgroundColor: '#2A3DBD', left: -4, borderTopRightRadius: '10px', borderBottomRightRadius: '10px' }} />}
                      <ListItemIcon
                        sx={[
                          {
                            minWidth: 0,
                            justifyContent: 'center',
                            pl: 1,
                          },
                          open
                            ? {
                              mr: 3,
                            }
                            : {
                              mr: 'auto',
                            },
                        ]}
                      >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke={content.label.includes("Settings") ? "rgba(42, 61, 189, 1)" : "#545454"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                          <path d="M19.4 15C19.2669 15.3016 19.2272 15.6362 19.286 15.9606C19.3448 16.285 19.4995 16.5843 19.73 16.82L19.79 16.88C19.976 17.0657 20.1235 17.2863 20.2241 17.5291C20.3248 17.7719 20.3766 18.0322 20.3766 18.295C20.3766 18.5578 20.3248 18.8181 20.2241 19.0609C20.1235 19.3037 19.976 19.5243 19.79 19.71C19.6043 19.896 19.3837 20.0435 19.1409 20.1441C18.8981 20.2448 18.6378 20.2966 18.375 20.2966C18.1122 20.2966 17.8519 20.2448 17.6091 20.1441C17.3663 20.0435 17.1457 19.896 16.96 19.71L16.9 19.65C16.6643 19.4195 16.365 19.2648 16.0406 19.206C15.7162 19.1472 15.3816 19.1869 15.08 19.32C14.7842 19.4468 14.532 19.6572 14.3543 19.9255C14.1766 20.1938 14.0813 20.5082 14.08 20.83V21C14.08 21.5304 13.8693 22.0391 13.4942 22.4142C13.1191 22.7893 12.6104 23 12.08 23C11.5496 23 11.0409 22.7893 10.6658 22.4142C10.2907 22.0391 10.08 21.5304 10.08 21V20.91C10.0723 20.579 9.96512 20.258 9.77251 19.9887C9.5799 19.7194 9.31074 19.5143 9 19.4C8.69838 19.2669 8.36381 19.2272 8.03941 19.286C7.71502 19.3448 7.41568 19.4995 7.18 19.73L7.12 19.79C6.93425 19.976 6.71368 20.1235 6.47088 20.2241C6.22808 20.3248 5.96783 20.3766 5.705 20.3766C5.44217 20.3766 5.18192 20.3248 4.93912 20.2241C4.69632 20.1235 4.47575 19.976 4.29 19.79C4.10405 19.6043 3.95653 19.3837 3.85588 19.1409C3.75523 18.8981 3.70343 18.6378 3.70343 18.375C3.70343 18.1122 3.75523 17.8519 3.85588 17.6091C3.95653 17.3663 4.10405 17.1457 4.29 16.96L4.35 16.9C4.58054 16.6643 4.73519 16.365 4.794 16.0406C4.85282 15.7162 4.81312 15.3816 4.68 15.08C4.55324 14.7842 4.34276 14.532 4.07447 14.3543C3.80618 14.1766 3.49179 14.0813 3.17 14.08H3C2.46957 14.08 1.96086 13.8693 1.58579 13.4942C1.21071 13.1191 1 12.6104 1 12.08C1 11.5496 1.21071 11.0409 1.58579 10.6658C1.96086 10.2907 2.46957 10.08 3 10.08H3.09C3.42099 10.0723 3.742 9.96512 4.0113 9.77251C4.28059 9.5799 4.48572 9.31074 4.6 9C4.73312 8.69838 4.77282 8.36381 4.714 8.03941C4.65519 7.71502 4.50054 7.41568 4.27 7.18L4.21 7.12C4.02405 6.93425 3.87653 6.71368 3.77588 6.47088C3.67523 6.22808 3.62343 5.96783 3.62343 5.705C3.62343 5.44217 3.67523 5.18192 3.77588 4.93912C3.87653 4.69632 4.02405 4.47575 4.21 4.29C4.39575 4.10405 4.61632 3.95653 4.85912 3.85588C5.10192 3.75523 5.36217 3.70343 5.625 3.70343C5.88783 3.70343 6.14808 3.75523 6.39088 3.85588C6.63368 3.95653 6.85425 4.10405 7.04 4.29L7.1 4.35C7.33568 4.58054 7.63502 4.73519 7.95941 4.794C8.28381 4.85282 8.61838 4.81312 8.92 4.68H9C9.29577 4.55324 9.54802 4.34276 9.72569 4.07447C9.90337 3.80618 9.99872 3.49179 10 3.17V3C10 2.46957 10.2107 1.96086 10.5858 1.58579C10.9609 1.21071 11.4696 1 12 1C12.5304 1 13.0391 1.21071 13.4142 1.58579C13.7893 1.96086 14 2.46957 14 3V3.09C14.0013 3.41179 14.0966 3.72618 14.2743 3.99447C14.452 4.26276 14.7042 4.47324 15 4.6C15.3016 4.73312 15.6362 4.77282 15.9606 4.714C16.285 4.65519 16.5843 4.50054 16.82 4.27L16.88 4.21C17.0657 4.02405 17.2863 3.87653 17.5291 3.77588C17.7719 3.67523 18.0322 3.62343 18.295 3.62343C18.5578 3.62343 18.8181 3.67523 19.0609 3.77588C19.3037 3.87653 19.5243 4.02405 19.71 4.21C19.896 4.39575 20.0435 4.61632 20.1441 4.85912C20.2448 5.10192 20.2966 5.36217 20.2966 5.625C20.2966 5.88783 20.2448 6.14808 20.1441 6.39088C20.0435 6.63368 19.896 6.85425 19.71 7.04L19.65 7.1C19.4195 7.33568 19.2648 7.63502 19.206 7.95941C19.1472 8.28381 19.1869 8.61838 19.32 8.92V9C19.4468 9.29577 19.6572 9.54802 19.9255 9.72569C20.1938 9.90337 20.5082 9.99872 20.83 10H21C21.5304 10 22.0391 10.2107 22.4142 10.5858C22.7893 10.9609 23 11.4696 23 12C23 12.5304 22.7893 13.0391 22.4142 13.4142C22.0391 13.7893 21.5304 14 21 14H20.91C20.5882 14.0013 20.2738 14.0966 20.0055 14.2743C19.7372 14.452 19.5268 14.7042 19.4 15Z" stroke={content.label.includes("Settings") ? "rgba(42, 61, 189, 1)" : "#545454"} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </ListItemIcon>
                      <ListItemText
                        primary={"Settings"}
                        sx={{
                          opacity: open ? 1 : 0,
                          '& .MuiTypography-root': {
                            fontFamily: 'Lato, sans-serif',
                            fontWeight: content.label.includes("Settings") ? 700 : 'normal',
                            color: content.label.includes("Settings") ? 'rgba(42, 61, 189, 1)' : 'inherit',
                          },
                        }}
                      />
                    </ListItemButton>
                  </ListItem>
                </List>
              )}
            </Box>
            <Box sx={{ pb: 10 }}>
              <List>
                <ListItem disablePadding sx={{ display: 'block' }}>
                  <ListItemButton
                    sx={[
                      {
                        minHeight: 48,
                        px: 2.5,
                      },
                      open
                        ? {
                          justifyContent: 'initial',
                        }
                        : {
                          justifyContent: 'center',
                        },
                    ]}
                    onClick={() => handleCollapseClick()}
                  >
                    <ListItemIcon
                      sx={[
                        {
                          minWidth: 0,
                          justifyContent: 'center',
                        },
                        open
                          ? {
                            mr: 3,
                          }
                          : {
                            mr: 'auto',
                          },
                      ]}
                    >
                      <svg width="36" height="36" viewBox="0 0 36 36" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M25.5 9L16.5 18L25.5 27" stroke="#545454" stroke-width="3" />
                        <path d="M9 25.5L9 10.5" stroke="#545454" stroke-width="3" />
                      </svg>
                    </ListItemIcon>
                    <ListItemText
                      primary={"Collapse"}
                      sx={{
                        opacity: open ? 1 : 0,
                        '& .MuiTypography-root': {
                          fontFamily: 'Lato, sans-serif',
                        },
                      }}
                    />
                  </ListItemButton>
                </ListItem>
                <ListItem disablePadding sx={{ display: 'block' }}>
                  <ListItemButton
                    sx={[
                      {
                        minHeight: 48,
                        px: 2.5,
                      },
                      open
                        ? {
                          justifyContent: 'initial',
                        }
                        : {
                          justifyContent: 'center',
                        },
                    ]}
                    onClick={() => handleLogout()}
                  >
                    <ListItemIcon
                      sx={[
                        {
                          minWidth: 0,
                          justifyContent: 'center',
                        },
                        open
                          ? {
                            mr: 3,
                          }
                          : {
                            mr: 'auto',
                          },
                      ]}
                    >
                      <svg style={{ marginRight: '5px', marginLeft: '5px' }} width="27" height="27" viewBox="0 0 27 27" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M20.25 9L24.75 13.5M24.75 13.5L20.25 18M24.75 13.5H10.125M16.875 4.72955C15.4409 3.86805 13.7759 3.375 12 3.375C6.61523 3.375 2.25 7.90812 2.25 13.5C2.25 19.0919 6.61523 23.625 12 23.625C13.7759 23.625 15.4409 23.1319 16.875 22.2705" stroke="#545454" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" />
                      </svg>
                    </ListItemIcon>
                    <ListItemText
                      primary={"Log out"}
                      sx={{
                        opacity: open ? 1 : 0,
                        '& .MuiTypography-root': {
                          fontFamily: 'Lato, sans-serif',
                        },
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              </List>
            </Box>
          </Drawer>
          <Box component="main" sx={{
            flexGrow: 1,
            width: '80%',
            px: content.label === "Welcome" ? 0 : 3
          }}>
            {content.component}
          </Box>
          <Modal
            aria-labelledby="modal-title"
            aria-describedby="modal-desc"
            open={isProfileOpen}
            onClose={() => setIsProfileOpen(false)}
            sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}
          >
            <Sheet
              variant="outlined"
              sx={{ width: '780px', borderRadius: 'md', boxShadow: 'lg' }}
            >
              <ModalClose size='lg' variant="plain" onClick={() => setIsProfileOpen(false)} />
              <Typography component="h2" sx={{
                fontFamily: 'Lato, sans-serif',
                fontSize: '16px',
                fontWeight: '700',
                color: '#000000',
                px: 3,
                py: 0.8,
                mt: 1
              }}>
                My Profile
              </Typography>
              <Divider sx={{ mb: 2, mt: 1 }} />
              <Box sx={{ display: 'flex', mb: 2, px: 3 }}>
                <Box sx={{ pr: 5, flex: 1 }}>
                  <AddUserLabel>Client ID</AddUserLabel>
                  <TextField
                    className='add-user-textfield'
                    name="clientId"
                    size="small"
                    placeholder="Enter Client ID"
                    fullWidth
                    disabled
                    value={userData.clientId || ''}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <AddUserLabel>User Name</AddUserLabel>
                  <TextField
                    name="userName"
                    size="small"
                    placeholder="Enter user name"
                    fullWidth
                    disabled
                    value={userData.userName || ''}
                  />
                </Box>
              </Box>
              <Box sx={{ display: 'flex', mb: 2, px: 3 }}>
                <Box sx={{ pr: 5, flex: 1 }}>
                  <AddUserLabel>Client name</AddUserLabel>
                  <TextField
                    name="fullname"
                    size="small"
                    placeholder="Enter Client Name"
                    fullWidth
                    disabled
                    value={userData.fullName || ''}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <AddUserLabel>Email address</AddUserLabel>
                  <TextField
                    name="email"
                    size="small"
                    placeholder="Enter email address"
                    fullWidth
                    disabled
                    value={userData.email || ''}
                  />
                </Box>
              </Box>
              {(userData?.roleCode === "CLTADM" || userData?.roleCode === "CLTUSR") &&
                <>
                  <Box sx={{ display: 'flex', mb: 4, px: 3 }}>
                    <Box sx={{ pr: 5, flex: 1 }}>
                      <AddUserLabel>NC Aging Target - Action</AddUserLabel>
                      <TextField
                        name="action-target"
                        type="number"
                        size="small"
                        value={target.aging_target_action}
                        placeholder="Enter aging target for action"
                        fullWidth
                        onChange={handleTargetChange("aging_target_action")}
                        error={errors.actionTarget}
                        helperText={errors.actionTarget ? "Aging target for action is required" : ""}
                        inputProps={{ min: 0 }}
                        onKeyDown={(e) => {
                          const invalidChars = ['-', '+', '.', 'e', 'E'];
                          if (invalidChars.includes(e.key)) {
                            e.preventDefault();
                          }
                        }}
                        disabled={userData.roleCode === "CLTUSR"}
                      />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <AddUserLabel>NC Aging Target - Review</AddUserLabel>
                      <TextField
                        name="review-target"
                        type="number"
                        size="small"
                        value={target.aging_target_review}
                        placeholder="Enter aging target for review"
                        fullWidth
                        onChange={handleTargetChange("aging_target_review")}
                        error={errors.reviewTarget}
                        helperText={errors.reviewTarget ? "Aging target for review is required" : ""}
                        inputProps={{ min: 0 }}
                        onKeyDown={(e) => {
                          const invalidChars = ['-', '+', '.', 'e', 'E'];
                          if (invalidChars.includes(e.key)) {
                            e.preventDefault();
                          }
                        }}
                        disabled={userData.roleCode === "CLTUSR"}
                      />
                    </Box>
                  </Box>
                  <Box sx={{ px: 3, mb: 3 }}>
                    <AddUserLabel sx={{ posistion: "relative", my: 2, display:"flex", alignItems:"center" }}>Profile Picture
                      {(userData.roleCode === 'CLTADM') && <Box
                        sx={{ position: "relative", display: "inline-block", ml: 1 }}
                        onMouseEnter={() => setIsProfileIconHovered(true)}
                        onMouseLeave={() => setIsProfileIconHovered(false)}
                      >
                        <CiCircleInfo
                          className="cursor-pointer"
                          size={18}
                          style={{ color: '#000', marginTop: 5, fontSize: 16, cursor: 'pointer' }}
                        />
                        {isProfileIconHovered && (
                          <Box
                            sx={{
                              position: "absolute",
                              display: "block",
                              top: "200%",
                              left: "100%",
                              transform: "translateY(-50%)",
                              marginLeft: 1,
                              backgroundColor: "white",
                              padding: "12px 16px",
                              borderRadius: "8px",
                              boxShadow: "0 4px 12px rgba(0, 0, 0, 0.15)",
                              border: "1px solid #e5e7eb",
                              fontSize: "12px",
                              width: "200px",
                              zIndex: 50,
                            }}
                          >
                            <Typography
                              sx={{
                                fontWeight: 600,
                                marginBottom: 2,
                                color: "#0E2966",
                                fontSize: "12px",
                              }}
                            >
                              File Requirements:
                            </Typography>
                            <Box sx={{ display: "flex", alignItems: "start", gap: 1 }}>
                              <ImageOutlined
                                sx={{
                                  color: "#3b82f6",
                                  marginRight: 1,
                                  marginTop: 0.5,
                                  fontSize: 16,
                                  flexShrink: 0,
                                }}
                              />
                              <Box sx={{ display: "flex", flexDirection: "column" }}>
                                <Typography
                                  sx={{
                                    fontWeight: 600,
                                    fontSize: "12px",
                                    color: "#1f2937",
                                  }}
                                >
                                  Images - Max 10MB
                                </Typography>
                                <Typography
                                  sx={{
                                    fontSize: "11px",
                                    color: "#747474",
                                    marginTop: 0.5,
                                  }}
                                >
                                  JPG, JPEG, PNG
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        )}
                      </Box>}
                    </AddUserLabel>

                    {(userData.roleCode === 'CLTADM') && (
                      <>
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: "center",
                            alignItems: 'center',
                            gap: 1.5,
                            width: 107,
                            height: 108,
                            mt: 1
                          }}
                        >
                          <Box
                            sx={{
                              position: 'relative',
                              border: '2px solid #c8cacf',
                              borderRadius: 3,
                              padding: '5px',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center'
                            }}
                          >
                            <Avatar
                              src={previewUrl || undefined}
                              imgProps={{
                                style: {
                                  objectFit: 'cover',
                                  objectPosition: 'center'
                                }
                              }}
                              sx={{
                                width: 100,
                                height: 100,
                                borderRadius: 3,
                              }}
                              variant="square"
                            />
                            {previewUrl && (
                              <IconButton
                                size="small"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  handleRemove();
                                }}
                                sx={{
                                  position: 'absolute',
                                  top: -5,
                                  right: -5,
                                  width: 25,
                                  height: 25,
                                  borderRadius: '50%',
                                  background: 'rgba(0,0,0,0.6)',
                                  color: 'white',
                                  zIndex: 10,
                                  '&:hover': { background: 'rgba(0,0,0,0.8)' },
                                }}
                              >
                                ✕
                              </IconButton>
                            )}
                          </Box>
                        </Box>
                        <Box sx={{ mt: 2 }}>
                          <input
                            ref={fileInputRef}
                            accept="image/*"
                            style={{ display: 'none' }}
                            id="profile-upload"
                            type="file"
                            onChange={handleFileChange}
                            disabled={isCompressingProfile}
                          />
                          <label htmlFor="profile-upload">
                            <Button
                              variant="outlined"
                              component="span"
                              disabled={isCompressingProfile}
                              sx={{
                                fontFamily: 'Lato, sans-serif',
                                fontSize: '14px',
                                fontWeight: '700',
                                border: '1px solid #0E2966 !important',
                                textTransform: 'none',
                                borderRadius: '7px',
                                color: '#0E2966 !important',
                                width: 108,
                              }}
                            >
                              {isCompressingProfile ? <CircularProgress size={20} /> : 'Upload'}
                            </Button>
                          </label>
                        </Box>
                      </>
                    )}

                    {userData.roleCode === 'CLTUSR' && (
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: "center",
                          alignItems: 'center',
                          gap: 1.5,
                          width: 107,
                          height: 108,
                          mt: 1
                        }}
                      >
                        <Box
                          sx={{
                            position: 'relative',
                            border: '2px solid #c8cacf',
                            borderRadius: 3,
                            padding: '5px',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                          }}
                        >
                          <Avatar
                            src={headerAvatar || undefined}
                            imgProps={{
                              style: {
                                objectFit: 'cover',
                                objectPosition: 'center'
                              }
                            }}
                            sx={{
                              width: 100,
                              height: 100,
                              borderRadius: 3,
                            }}
                            variant="square"
                          />
                        </Box>
                      </Box>
                    )}
                  </Box>

                  {userData.roleCode !== "CLTUSR" &&
                    <Box sx={{
                      borderRadius: '0px 0px 10px 10px',
                      boxShadow: '0px -1px 4px 0px #00000026',
                      textAlign: 'right',
                      px: 3,
                      py: 1.5,
                      mt: 3,
                    }}>
                      <Button
                        variant="outlined"
                        color="neutral"
                        sx={{
                          fontFamily: 'Lato, sans-serif',
                          fontSize: '14px',
                          fontWeight: '700',
                          border: '1px solid #0E2966 !important',
                          textTransform: 'none',
                          borderRadius: '7px',
                          color: '#0E2966 !important',
                          mr: 2
                        }}
                        onClick={async () => setIsProfileOpen(false)}
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleTarget}
                        sx={{
                          fontFamily: 'Lato, sans-serif',
                          background: 'linear-gradient(270.29deg, #43B0D7 0.24%, #0D2663 99.75%)',
                          color: 'white',
                          textTransform: 'none',
                          borderRadius: '7px',
                          px: 2,
                        }}
                      >
                        Save
                      </Button>
                    </Box>}
                </>}
            </Sheet>
          </Modal>
        </Box>
      )
      }
    </Box >
  );
};

